import * as amplitude from '@amplitude/analytics-browser';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import audioSamples from '../data/mapped_stories.json';
import { highlightText, Quiz, quizSteps } from '../data/quiz';
import { AudioTrack } from '../shared/audio-track/audio-track.component';
import { UserReview } from '../shared/user-review/user-review.component';
import { EventsService } from './events.service';

export type QuizStepKey = keyof typeof quizSteps;

const initialQuizStepOrder: QuizStepKey[] = [
  'age',

  'relationshipStatus',

  'anxiety',

  'socialProof',

  'fantasies',

  'amountInfo',

  'fantasiesFulfillment',

  'audioInfo',

  'orgasms',

  'experience',
  'kinks',

  'imagination',

  'medicalFact',

  'storiesSample1',
  'storiesSample2',
  'soundsSample1',
  'soundsSample2',

  'duration',
  'intensity',
  'pov',

  'honest',
  'result',
];

type QuizSteps = {
  [K in QuizStepKey]: Quiz;
};

@Injectable({
  providedIn: 'root'
})
export class OnboardingFlowService {
  get fullScreenBackgroundImage$(): Observable<string | null> {
    return this.bgImageSubject.asObservable();
  }

  private steps: QuizSteps = quizSteps;

  private age: string;
  private experiences: string[] = [];
  private kinks: string[] = [];

  private name: string | undefined;
  private stripeCustomerId: string | undefined;

  private topTitle = 'Unlock Your Potential';

  private paywallLevelTitle: string;
  private paywallImprovementTitle: string;

  private quizStepOrder: QuizStepKey[];

  private bgImageSubject = new BehaviorSubject<string | null>(null);

  constructor(private router: Router, private pixel: EventsService) {
    this.quizStepOrder = [...initialQuizStepOrder];

    //if (this.abTestService.getTestingProps().isGoalScreenFirst) {
  }

  goToStart() {
    this.router.navigate(['start']);
  }

  setBackgroundImage(url: string | null): void {
    this.bgImageSubject.next(url);
  }

  handleFinishedStartScreen() {
    amplitude.track('web_start_screen_viewed');
    this.router.navigate(['quiz', 0]);
  }

  handleFinishedCustomization() {
    //const indexToGo = this.quizStepOrder.indexOf('result');

    amplitude.track('web_customization_screen_viewed');

    //this.router.navigate(['quiz', indexToGo]);
    this.router.navigate(['login']);
  }

  handleFinishedLogin(stripeCustomerId: string) {
    const indexToGo = this.quizStepOrder.indexOf('result');
    
    this.stripeCustomerId = stripeCustomerId;
    //event of login is fired in auth service
    
    this.router.navigate(['quiz', indexToGo]);
  }

  handleFinishedStep(index: number, options: string[]) {
     const key = this.quizStepOrder[index];

     this.trackResponse(key, options);
     this.saveParameters(key, options);
     
     if (index + 1 >= this.quizStepOrder.length) {
      this.router.navigate(['checkout']);
     } else if (key === 'honest') {
      this.router.navigate(['ready']);
     //} else if (key === 'screenTimeImprovement') {
     // this.router.navigate(['login']);
     } else {
      this.router.navigate(['quiz', index + 1]);
     }
  }

  getName(): string | undefined {
    return this.name;
  }

  getStripeCustomerId(): string | undefined {
    return this.stripeCustomerId;
  }

  private customizeStepData(key: QuizStepKey): Quiz | undefined {
    switch (key) {
      case 'amountInfo':
        const count = this.age === '21-34' ? '600K' : (this.age === '35-44' ? '500K' : '800K');
        const title = 'Women';

        return {
          ...this.steps.amountInfo,
          title: `Join ${highlightText(`Over ${count} ${title}`)} your age with similar interests worldwide`,
        };

        case 'storiesSample1':
          return {
            ...this.steps.storiesSample1,
            audioTracks: [this.getSample('story', 0)],
          };
    
        case 'storiesSample2':
          return {
            ...this.steps.storiesSample2,
            audioTracks: [this.getSample('story', 1)],
          };
    
        case 'soundsSample1':
          return {
            ...this.steps.soundsSample1,
            audioTracks: [this.getSample('sounds', 0)],
          };
    
        case 'soundsSample2':
          return {
            ...this.steps.soundsSample2,
            audioTracks: [this.getSample('sounds', 1)],
          };

      default:
        return;
    }
  }

  private shouldSkipStep(key: QuizStepKey): Boolean {
    switch(key) {
      default:
        return false;
    }
  }

  getQuizData(index: number): Quiz {
    const key = this.quizStepOrder[index];

    if(this.shouldSkipStep(key) && index < this.getQuizLength() - 1) {
      this.router.navigate(['quiz', index + 1]);
    }

    return this.customizeStepData(key) || this.steps[key];
  }

  getQuizLength(): number {
    return this.quizStepOrder.length;
  }

  rememberLastPlayedTrack(audio: AudioTrack): void {
    amplitude.track('web_audio_demo_played', { title: audio.title });
  }

  getTopTitle(): string {
    return this.topTitle;
  }

  getPaywallComparisonLabels(): { level: string, graph: string } {
    return {
      level: this.paywallLevelTitle || 'Procrastination',
      graph: this.paywallImprovementTitle || 'Comprehension',
    }
  }

  getPaywallAudioSamples(): AudioTrack[] {
    const samples = this.getSamples();
    const shuffled = samples.sort(() => 0.5 - Math.random());
    
    return shuffled.slice(0, 3);
  }

  private saveParameters(currentStepKey: QuizStepKey, selectedOptions: string[]) {
    switch (currentStepKey) {
      case 'age':
        this.age = selectedOptions[0];
        break;
      case 'experience':
        this.experiences = selectedOptions;
        break;
      case 'kinks':
        this.kinks = selectedOptions;
        break;
    }
  }
  
  private getSample(type: 'story' | 'sounds', index: number): AudioTrack {
    const samples = this.getSamples(type);

    return samples[index] || this.getDefaultSamples(type)[index];
  }

  private getSamples(type?: 'story' | 'sounds'): AudioTrack[] {
    const currentTags = [...this.experiences, ...this.kinks].map(tag => tag.toLowerCase());

    if (currentTags.length === 0) {
      return this.getDefaultSamples(type);
    }

    const relevantSounds = audioSamples
      .map(story => ({
      ...story,
        audioUrl: story.audioTeaserUrl,
        languageIntensity: story.languageIntensity.charAt(0).toUpperCase() + story.languageIntensity.slice(1),
        tags: story.tags.map(tag => tag.toLowerCase()),
      } as AudioTrack))
      .filter(story => 
        currentTags.some(tag => story.tags.includes(tag)) && (type ? story.type === type : true)
      );

    if (relevantSounds.length === 0) {
      return this.getDefaultSamples(type);
    }

    return relevantSounds;
  }

  private getDefaultSamples(type?: 'story' | 'sounds'): AudioTrack[] {
    const relevantSounds = audioSamples
      .map(story => ({
        ...story,
        languageIntensity: story.languageIntensity.charAt(0).toUpperCase() + story.languageIntensity.slice(1),
        audioUrl: story.audioTeaserUrl,
      } as AudioTrack))
      .filter(story => (type ? story.type === type : true));

    return relevantSounds;
  }

  private getUserReview(): UserReview {
    const review = {
      name: 'Jenny Barnes',
      avatarUrl: 'assets/img/avatars/5.png',
      rate: 5,
    }

    return {
      ...review,
      message: 'FemSecret has given me the edge in my work, helping me absorb concepts on the go.',
    };
  }

  private removeEmojis(string: string): string {
    return string.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}]/gu, '').trim();
  }

  private trackResponse(currentStepKey: QuizStepKey, options: string[]): void {
    amplitude.track('web_quiz_step_finished', { key: currentStepKey, options });
  }
}
